import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, ChevronDown } from 'lucide-react';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1200);
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1200);
      if (window.innerWidth >= 1200) {
        setIsOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navItems = [
    { 
      name: 'Beskæftigelsen - hvem er vi', 
      path: '/beskaeftigelsen',
      children: [
        { 
          name: 'Paragraf 103', 
          path: '/paragraf-103',
          description: 'Lovgrundlag, visitation og støtte'
        },
      ]
    },
    { 
      name: 'Målgruppe', 
      path: '/maalgruppe',
      children: [
        { 
          name: 'Aktivitetstilbud', 
          path: '/aktivitetstilbud',
          description: 'Beskyttet beskæftigelse i trygge rammer'
        },
      ]
    },
    { name: 'Kerneværdier', path: '/vaerdigrundlag' },
    { name: 'Pædagogik', path: '/paedagogik' },
    { name: 'STU', path: '/stu' },
    { name: 'Billeder', path: '/billeder' },
    { name: 'Kontakt', path: '/kontakt' },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path;
  };

  return (
    <header className="relative z-50 w-full bg-stone-50 border-b border-stone-200">
      {/* Top Utility Bar */}
      <div className="bg-stone-900 text-stone-300 text-xs py-2 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-2 text-stone-400">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>Privat Beskyttet Beskæftigelse — §103</span>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-1">
            <a 
              href="tel:51504654" 
              className="flex items-center gap-1.5 hover:text-emerald-400 transition-colors whitespace-nowrap"
            >
              <Phone className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <span className="whitespace-nowrap">51 50 46 54</span>
            </a>
            <a 
              href="mailto:Kontakt@livslystaps.dk" 
              className="flex items-center gap-1.5 hover:text-emerald-400 transition-colors whitespace-nowrap"
            >
              <Mail className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <span className="whitespace-nowrap">Kontakt@livslystaps.dk</span>
            </a>
            <span className="hidden md:flex items-center gap-1.5 text-stone-400 whitespace-nowrap">
              <MapPin className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <span>Surhavevej 14 A, 6650 Brørup</span>
            </span>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group shrink-0">
            <img 
              src="https://assets.auxodev.dk/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/bc48b260-e651-4247-ad1f-cf4285cef198.png" 
              alt="LIVSLYST ApS Logo" 
              className="h-16 md:h-20 w-auto object-contain transition-transform duration-200 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation (Visible only at 1200px and above) */}
          {isLargeScreen && (
            <nav className="flex items-center gap-1">
              {navItems.map((item) => {
                if (item.children) {
                  const isParentActive = location.pathname === item.path || item.children.some(child => location.pathname === child.path);
                  return (
                    <div key={item.path} className="relative group py-2">
                      <Link
                        to={item.path}
                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                          isParentActive
                            ? 'bg-emerald-50 text-emerald-800 border-b-2 border-emerald-700 rounded-b-none'
                            : 'text-stone-600 hover:text-stone-900 hover:bg-stone-100'
                        }`}
                      >
                        <span>{item.name}</span>
                        <ChevronDown className="w-3.5 h-3.5 text-stone-500 transition-transform duration-200 group-hover:rotate-180 group-hover:text-emerald-700" />
                      </Link>

                      {/* Dropdown Menu with hover trigger */}
                      <div className="absolute top-full left-0 pt-1.5 w-72 opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto transition-all duration-200 z-50 transform origin-top-left">
                        <div className="bg-white rounded-xl shadow-xl border border-stone-200 p-2 space-y-1">
                          {item.children.map((child) => {
                            const isChildActive = location.pathname === child.path;
                            return (
                              <Link
                                key={child.path}
                                to={child.path}
                                className={`block px-3.5 py-2.5 rounded-lg transition-all ${
                                  isChildActive
                                    ? 'bg-emerald-50 text-emerald-900 font-medium'
                                    : 'text-stone-700 hover:bg-stone-50 hover:text-emerald-700'
                                }`}
                              >
                                <div className="text-sm font-semibold flex items-center justify-between">
                                  <span>{child.name}</span>
                                  {isChildActive && (
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" />
                                  )}
                                </div>
                                {child.description && (
                                  <p className="text-xs text-stone-500 mt-0.5 font-normal leading-snug">
                                    {child.description}
                                  </p>
                                )}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      isActive(item.path)
                        ? 'bg-emerald-50 text-emerald-800 border-b-2 border-emerald-700 rounded-b-none'
                        : 'text-stone-600 hover:text-stone-900 hover:bg-stone-100'
                    }`}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          )}

          {/* Mobile Menu Button (Visible below 1200px) */}
          {!isLargeScreen && (
            <div className="flex">
              <button
                onClick={() => setIsOpen(!isOpen)}
                type="button"
                className="inline-flex items-center justify-center p-2.5 rounded-md text-stone-600 hover:text-stone-900 hover:bg-stone-100 focus:outline-none transition-colors"
                aria-controls="mobile-menu"
                aria-expanded={isOpen}
              >
                <span className="sr-only">Åbn menu</span>
                {isOpen ? <X className="block h-6 w-6" /> : <Menu className="block h-6 w-6" />}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Menu (Slide down, below 1200px) */}
      {!isLargeScreen && isOpen && (
        <div className="border-t border-stone-200 bg-stone-50 shadow-inner" id="mobile-menu">
          <div className="px-2 pt-3 pb-4 space-y-1 sm:px-3">
            {navItems.map((item) => {
              if (item.children) {
                return (
                  <div key={item.path} className="space-y-1 pb-1">
                    <Link
                      to={item.path}
                      onClick={() => setIsOpen(false)}
                      className={`block px-4 py-2.5 rounded-md text-base font-medium transition-colors ${
                        location.pathname === item.path
                          ? 'bg-emerald-50 text-emerald-800 font-semibold'
                          : 'text-stone-700 hover:text-stone-900 hover:bg-stone-100'
                      }`}
                    >
                      {item.name}
                    </Link>
                    <div className="pl-3 border-l-2 border-emerald-300 ml-6 space-y-1 my-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={() => setIsOpen(false)}
                          className={`block px-3 py-2 rounded-md text-sm transition-colors ${
                            location.pathname === child.path
                              ? 'bg-emerald-50 text-emerald-800 font-semibold'
                              : 'text-stone-600 hover:text-stone-900 hover:bg-stone-100'
                          }`}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-3 rounded-md text-base font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-emerald-50 text-emerald-800 font-semibold'
                      : 'text-stone-600 hover:text-stone-900 hover:bg-stone-100'
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}
